import { ChevronLeft, ChevronRight, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { formatINR } from "@/lib/qgCalc";
import { STATUS_META } from "@/lib/qgApi";
import { GroupBlock } from "./GroupBlock";
import { NotesEditor } from "./NotesEditor";

export function StatusChip({ status, testId }) {
  const m = STATUS_META[status] || STATUS_META.not_started;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${m.text}`} data-testid={testId}>
      <span className={`h-2 w-2 rounded-full ${m.dot}`} />{m.label}
    </span>
  );
}

function FasSubtotals({ catCalc }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3" data-testid="qg-fas-subtotals">
      {[["fire", "Fire detection system"], ["cctv", "CCTV"]].map(([k, l]) => (
        <div key={k} className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3 bg-white dark:bg-slate-800">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total of {l}</p>
          <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-white" data-testid={`qg-fas-subtotal-${k}`}>{formatINR(catCalc.subtotals[k] || 0n, true)}</p>
        </div>
      ))}
      <div className="rounded-lg border border-brillien-200 px-4 py-3 bg-brillien-50 dark:bg-brillien-900/20">
        <p className="text-[10px] uppercase tracking-wider text-brillien-700 font-semibold">Total of FAS / CCTV</p>
        <p className="text-lg font-bold tabular-nums text-brillien-700" data-testid="qg-fas-total">{formatINR(catCalc.total, true)}</p>
      </div>
    </div>
  );
}

export function SectionPanel({ section, code, catCalc, readOnly, actions, onPrev, onNext, prevLabel, nextLabel }) {
  const blocking = catCalc.errors.filter((e) => e.field !== "reviewed");
  const markReviewed = () => {
    if (blocking.length) {
      toast.error(`${blocking.length} issue(s) left in ${section.name}. Fix the highlighted fields first.`);
      document.getElementById(blocking[0].item_id ? `qg-item-${blocking[0].item_id}` : `qg-group-${blocking[0].group_id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      actions.setSection(section.key, { reviewed: true }, true);
      return;
    }
    actions.setSection(section.key, { reviewed: true }, true);
    toast.success(`${section.name} marked complete`);
  };

  return (
    <div className="space-y-8" data-testid={`qg-section-panel-${section.key}`}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs text-brillien-600 font-semibold">SECTION {code}</p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{section.name}</h2>
          <div className="flex items-center gap-3 mt-1">
            <StatusChip status={catCalc.status} testId={`qg-section-status-${section.key}`} />
            <span className="text-xs text-slate-400">Template tab: {section.sheet_name}</span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Section total</p>
          <p className="text-2xl font-bold tabular-nums text-slate-900 dark:text-white" data-testid={`qg-section-total-${section.key}`}>
            {section.not_applicable ? "Not included" : formatINR(catCalc.total, true)}
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <Switch checked={!!section.not_applicable} disabled={readOnly} data-testid={`qg-section-na-${section.key}`}
            onCheckedChange={(v) => actions.setSection(section.key, { not_applicable: v, reviewed: v ? true : section.reviewed }, true)} />
          <Ban className="h-4 w-4 text-slate-400" />Not applicable for this project
        </label>
        {!section.not_applicable && (
          <span className="text-xs text-slate-500" data-testid={`qg-section-issues-${section.key}`}>{blocking.length ? `${blocking.length} issue(s) to resolve` : "All included items are complete"}</span>
        )}
        {!readOnly && !section.not_applicable && (
          section.reviewed ? (
            <Button variant="outline" size="sm" onClick={() => actions.setSection(section.key, { reviewed: false }, true)} className="ml-auto rounded-lg" data-testid={`qg-section-reopen-${section.key}`}>Reopen section</Button>
          ) : (
            <Button size="sm" onClick={markReviewed} className="ml-auto rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`qg-section-complete-${section.key}`}>
              <CheckCircle2 className="h-4 w-4 mr-1.5" />Mark section complete
            </Button>
          )
        )}
      </div>

      {section.not_applicable ? (
        <div className="rounded-xl border border-dashed border-slate-300 py-12 text-center text-sm text-slate-500" data-testid={`qg-section-na-message-${section.key}`}>
          Marked Not applicable — shown as “Not included” in the summary and omitted from the PDF.
        </div>
      ) : (
        <>
          {catCalc.errors.some((e) => e.field === "items") && <p className="text-sm text-red-600">All items are excluded — add items or mark the section Not applicable.</p>}
          {section.groups.map((g) => <GroupBlock key={g.id} section={section} group={g} catCalc={catCalc} readOnly={readOnly} actions={actions} />)}
          {section.key === "fas_cctv" && <FasSubtotals catCalc={catCalc} />}
          <NotesEditor title={`${section.name} notes`} notes={section.notes} readOnly={readOnly} onChange={(n) => actions.setNotes(section.key, n)} testId={`qg-section-notes-${section.key}`} />
        </>
      )}

      <footer className="flex justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
        <Button variant="ghost" onClick={onPrev} data-testid="qg-prev-btn" className="rounded-lg"><ChevronLeft className="h-4 w-4 mr-1" />{prevLabel}</Button>
        <Button variant="ghost" onClick={onNext} data-testid="qg-next-btn" className="rounded-lg">{nextLabel}<ChevronRight className="h-4 w-4 ml-1" /></Button>
      </footer>
    </div>
  );
}
